// Iter 156.34 (Feb 28, 2026, Yash) — TEMPORARY debug page for the
// cancel-upstream audit log.
//
// Production issue: orders 2895955 / 2876250 were cancelled in the CRM
// but the upstream admin_crm.php access log shows no incoming request
// for those IDs. This page surfaces every attempted call (validation,
// pre-send, post-send, exceptions) so the operator can pinpoint where
// the chain breaks.
//
// To be removed once the bug is identified.
//
// Source endpoint: GET /api/admin/cancel-upstream-logs
//   Filters: order_id, phase, since_hours, limit
import { useState, useEffect, useCallback } from "react";
import { adminApi } from "../lib/api";
import { ChevronDown, ChevronRight, RefreshCw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const PHASE_BADGES = {
  pre_send:             { bg: "bg-blue-100",    fg: "text-blue-800",    label: "Pre-send" },
  post_send:            { bg: "bg-green-100",   fg: "text-green-800",   label: "Success" },
  upstream_error:       { bg: "bg-red-100",     fg: "text-red-800",     label: "Upstream error" },
  validation_failed:    { bg: "bg-yellow-100",  fg: "text-yellow-800",  label: "Validation failed" },
  post_send_error:      { bg: "bg-orange-100",  fg: "text-orange-800",  label: "Network/transport error" },
  unexpected_exception: { bg: "bg-pink-100",    fg: "text-pink-800",    label: "Unexpected exception" },
};

function PhaseBadge({ phase }) {
  const cfg = PHASE_BADGES[phase] || { bg: "bg-gray-100", fg: "text-gray-800", label: phase || "—" };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.fg}`}
      data-testid={`phase-badge-${phase || "unknown"}`}
    >
      {cfg.label}
    </span>
  );
}

function HttpBadge({ status }) {
  if (status == null) return <span className="text-gray-400">—</span>;
  const num = Number(status);
  const bg = num >= 200 && num < 300 ? "bg-green-100 text-green-800"
           : num >= 400              ? "bg-red-100 text-red-800"
           :                            "bg-gray-100 text-gray-800";
  return <span className={`px-2 py-0.5 rounded text-xs font-mono ${bg}`} data-testid="http-badge">{num}</span>;
}

function RowDetails({ row }) {
  return (
    <div className="p-4 bg-gray-50 border-t border-gray-200 text-sm space-y-3" data-testid="row-details">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="font-semibold text-gray-700 mb-1">Request</div>
          <div className="text-xs text-gray-500 mb-1">URL</div>
          <code className="block bg-white border border-gray-200 rounded px-2 py-1 text-xs break-all font-mono">
            {row.url || "—"}
          </code>
          <div className="text-xs text-gray-500 mt-2 mb-1">Form payload</div>
          <pre className="bg-white border border-gray-200 rounded px-2 py-1 text-xs overflow-auto max-h-48 font-mono">
            {row.form ? JSON.stringify(row.form, null, 2) : "—"}
          </pre>
          {row.elapsed_ms != null && (
            <div className="text-xs text-gray-500 mt-2">
              Elapsed: <span className="font-mono">{Math.round(row.elapsed_ms)} ms</span>
            </div>
          )}
        </div>

        <div>
          <div className="font-semibold text-gray-700 mb-1">Response</div>
          <div className="text-xs text-gray-500 mb-1">Parsed JSON</div>
          <pre className="bg-white border border-gray-200 rounded px-2 py-1 text-xs overflow-auto max-h-48 font-mono">
            {row.parsed_data ? JSON.stringify(row.parsed_data, null, 2) : "—"}
          </pre>
          <div className="text-xs text-gray-500 mt-2 mb-1">Raw body</div>
          <pre className="bg-white border border-gray-200 rounded px-2 py-1 text-xs overflow-auto max-h-48 font-mono whitespace-pre-wrap">
            {row.raw_response_text || "—"}
          </pre>
        </div>
      </div>

      {row.error && (
        <div>
          <div className="font-semibold text-red-700 mb-1">Error</div>
          <pre className="bg-red-50 border border-red-200 rounded px-2 py-1 text-xs whitespace-pre-wrap text-red-800">
            {row.error}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function CancelUpstreamLogsPage() {
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("");
  const [sinceHours, setSinceHours] = useState("24");
  const [limit, setLimit] = useState(200);
  const [rows, setRows] = useState([]);
  const [phaseCounts, setPhaseCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [clearing, setClearing] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (orderIdFilter.trim()) params.order_id = orderIdFilter.trim();
      if (phaseFilter) params.phase = phaseFilter;
      if (sinceHours && Number(sinceHours) > 0) params.since_hours = Number(sinceHours);
      const { data } = await adminApi.getCancelUpstreamLogs(params);
      setRows(data?.rows || []);
      setPhaseCounts(data?.phase_counts || {});
    } catch (e) {
      toast.error(`Failed to load logs: ${e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [orderIdFilter, phaseFilter, sinceHours, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleClear = async () => {
    const olderThan = window.prompt(
      "Delete cancel-upstream logs older than N hours?\n\n" +
      "Enter 0 to delete ALL logs.\n" +
      "Enter e.g. 168 to keep last 7 days.\n",
      "0"
    );
    if (olderThan === null) return;
    const n = parseInt(olderThan, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Invalid number");
      return;
    }
    if (!window.confirm(`Delete logs older than ${n} hours? This cannot be undone.`)) return;
    setClearing(true);
    try {
      const { data } = await adminApi.clearCancelUpstreamLogs(n);
      toast.success(`Deleted ${data?.deleted || 0} log rows`);
      await fetchLogs();
    } catch (e) {
      toast.error(`Failed to clear: ${e?.response?.data?.detail || e?.message || e}`);
    } finally {
      setClearing(false);
    }
  };

  const toggleRow = (id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="cancel-upstream-logs-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">
          Cancel Upstream Logs
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Temporary debug audit of every attempted call to{" "}
          <code className="bg-gray-100 px-1 rounded text-xs">
            admin_crm.php?action=order&amp;subaction=cancel
          </code>
          . Every CRM-side cancel attempt logs a row here — validation rejections, pre-send intent,
          post-send outcome, and exceptions. Will be removed once the root cause is identified.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="filters-row">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Order ID</label>
            <input
              type="text"
              value={orderIdFilter}
              onChange={(e) => setOrderIdFilter(e.target.value)}
              placeholder="e.g. 2895955"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              data-testid="filter-order-id"
              onKeyDown={(e) => e.key === "Enter" && fetchLogs()}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phase</label>
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              data-testid="filter-phase"
            >
              <option value="">All</option>
              <option value="pre_send">Pre-send</option>
              <option value="post_send">Success</option>
              <option value="upstream_error">Upstream error</option>
              <option value="validation_failed">Validation failed</option>
              <option value="post_send_error">Network/transport error</option>
              <option value="unexpected_exception">Unexpected exception</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Since (hours)</label>
            <input
              type="number"
              value={sinceHours}
              onChange={(e) => setSinceHours(e.target.value)}
              placeholder="24"
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              data-testid="filter-since-hours"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Limit</label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || 200)}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              data-testid="filter-limit"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex-1 inline-flex items-center justify-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-medium text-gray-700">Phase counts:</span>
          {Object.keys(phaseCounts).length === 0 && <span className="text-gray-500">no rows</span>}
          {Object.entries(phaseCounts).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1">
              <PhaseBadge phase={k} />
              <span className="font-mono">×{v}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="logs-table">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">When</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Order</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Item</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Action</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Phase</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">HTTP</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Elapsed</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Summary</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-gray-500" data-testid="empty-row">
                    No log rows for the current filters.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const isOpen = !!expanded[row.id];
                const summary =
                  row.error ? (row.error.length > 100 ? row.error.slice(0, 100) + "…" : row.error)
                  : row.parsed_data?.message ? row.parsed_data.message
                  : row.parsed_data?.refund_id ? `refund_id: ${row.parsed_data.refund_id}`
                  : row.phase === "pre_send" ? "(intent recorded — post_send row will follow)"
                  : "—";
                return (
                  <>
                    <tr
                      key={row.id}
                      className={`hover:bg-gray-50 cursor-pointer ${isOpen ? "bg-blue-50/40" : ""}`}
                      onClick={() => toggleRow(row.id)}
                      data-testid={`log-row-${row.orderid || "noid"}`}
                    >
                      <td className="px-3 py-2 text-gray-400">
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-gray-700">
                        {fmtDT(row.logged_at)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{row.orderid || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.itemcode || "—"}</td>
                      <td className="px-3 py-2 text-xs">{row.action || "—"}</td>
                      <td className="px-3 py-2"><PhaseBadge phase={row.phase} /></td>
                      <td className="px-3 py-2"><HttpBadge status={row.http_status} /></td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {row.elapsed_ms != null ? `${Math.round(row.elapsed_ms)} ms` : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700 max-w-md truncate">
                        {summary}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={row.id + "-details"}>
                        <td colSpan={9} className="p-0">
                          <RowDetails row={row} />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Danger zone */}
      <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between" data-testid="danger-zone">
        <div className="text-xs text-red-800">
          <strong>Cleanup</strong> — once the root cause is identified, clear this audit collection to free storage.
        </div>
        <button
          onClick={handleClear}
          disabled={clearing}
          className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded disabled:opacity-50"
          data-testid="clear-logs-btn"
        >
          <Trash2 size={12} />
          {clearing ? "Clearing…" : "Clear logs"}
        </button>
      </div>
    </div>
  );
}
